import { useRef, useState, useEffect, ReactNode, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { parseFromString } from "@ayla/conversation";
import { extractAxiosErrorMessage } from "@ayla/api";
import { Button } from "@/components/ui/button";
import { setTranscript, setServerAnalysis } from "@/state/transcriptStore";
import { useToast } from "@/hooks/use-toast";
import { PrivacyBanner } from "./PrivacyBanner";
import { loadHistory, saveHistory } from "@/utils/history";
import { apiClient } from "@/lib/apiClient";
import { useAuthStore } from "@/state/authStore";
import type { Transcript, TranscriptEntry } from "@ayla/conversation";
import type { ServerAnalysis } from "@ayla/conversation";
import type { EncounterSnapshot } from "@ayla/conversation";
import { FeedbackCard } from "@/components/FeedbackCard";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Mic, FileText, Loader2, AlertTriangle } from "lucide-react";
import { getApiBaseUrl } from "@ayla/config";
import {
  isPaceResponse,
  mapPaceResponseToServerAnalysis,
} from "@/utils/paceAnalysis";
import { getPaceAnalysisId } from "@/utils/paceHistory";

// ErrorKind type removed - currently unused

type TranscriptLike = {
  entries?: Array<Partial<TranscriptEntry>>;
};

const AUDIO_EXTS = [".wav", ".mp3", ".m4a", ".mp4", ".webm", ".ogg"];
const TX_EXTS = [".json", ".md", ".srt", ".txt"];
const MAX_AUDIO_BYTES = 100 * 1024 * 1024; // 100 MB
const MAX_TEXT_BYTES = 10 * 1024 * 1024; // 10 MB
const BACKEND_URL = getApiBaseUrl();

export function UploadTranscript() {
  const [error, setError] = useState<ReactNode | null>(null);
  const [loading, setLoading] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [openPickerAfterShow, setOpenPickerAfterShow] = useState(false);
  const [lastFilename, setLastFilename] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { toast } = useToast();
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [txPreview, setTxPreview] = useState<{
    words: number;
    preview: string;
  } | null>(null);
  const [audioMeta, setAudioMeta] = useState<{ duration?: number } | null>(
    null
  );
  const [dragOver, setDragOver] = useState(false);

  const formatDur = (sec?: number) => {
    if (!sec || !isFinite(sec)) return undefined;
    const m = Math.floor(sec / 60);
    const s = Math.round(sec % 60);
    return m ? `${m}m ${s}s` : `${s}s`;
  };

  const acceptAndQueue = (file: File) => {
    setError(null);
    const audio = isAudioFile(file.name);
    const textTx = isTranscriptFile(file.name);

    if (!audio && !textTx) {
      setError(renderUnsupported());
      return;
    }
    if (audio && file.size > MAX_AUDIO_BYTES) {
      setError(renderTooLarge(bytesToMB(file.size), true));
      return;
    }
    if (textTx && file.size > MAX_TEXT_BYTES) {
      setError(renderTooLarge(bytesToMB(file.size), false));
      return;
    }

    setPendingFile(file);
  };

  // ---- Config / helpers ----------------------------------------------------
  const isAudioFile = useCallback(
    (name: string) => AUDIO_EXTS.some(ext => name.toLowerCase().endsWith(ext)),
    []
  );
  const isTranscriptFile = useCallback(
    (name: string) => TX_EXTS.some(ext => name.toLowerCase().endsWith(ext)),
    []
  );

  const bytesToMB = (b: number) => Math.round(b / 1024 / 1024);

  // ---- Rich error renderers ------------------------------------------------
  const renderUnsupported = (): ReactNode => (
    <div className="space-y-2">
      <div className="font-semibold">❌ File type not supported</div>
      <div className="text-xs text-foreground/80">
        <div className="font-medium">We accept:</div>
        <ul className="list-disc ml-5">
          <li>Audio: WAV, MP3, M4A, MP4, WEBM, OGG</li>
          <li>Transcripts: JSON, Markdown (.md), SRT, TXT</li>
        </ul>
        <div className="mt-2">Please select a supported file.</div>
      </div>
    </div>
  );

  const renderTooLarge = (sizeMB: number, audio: boolean): ReactNode => (
    <div className="space-y-2">
      <div className="font-semibold">❌ File is too large ({sizeMB} MB)</div>
      <div className="text-xs text-foreground/80">
        <div className="font-medium">Maximum sizes:</div>
        <ul className="list-disc ml-5">
          <li>Audio files: 100 MB</li>
          <li>Transcripts: 10 MB</li>
        </ul>
        {audio ? (
          <div className="mt-2">
            Try compressing your audio or splitting the recording.
          </div>
        ) : null}
      </div>
    </div>
  );

  const renderTranscribeFailed = (): ReactNode => (
    <div className="space-y-2">
      <div className="font-semibold">❌ We couldn’t transcribe the audio</div>
      <div className="text-xs text-foreground/80">
        <div>This might be because:</div>
        <ul className="list-disc ml-5">
          <li>The audio quality is too low</li>
          <li>There’s too much background noise</li>
          <li>The file is corrupted</li>
        </ul>
        <div className="mt-2">Try recording in a quieter environment.</div>
      </div>
    </div>
  );

  const renderInvalidTranscript = (): ReactNode => (
    <div className="space-y-2">
      <div className="font-semibold">❌ We couldn’t parse the transcript</div>
      <div className="text-xs text-foreground/80">
        <div>The file structure might be incorrect. Try:</div>
        <ul className="list-disc ml-5">
          <li>Checking the JSON structure</li>
          <li>Using our sample format</li>
          <li>Uploading the raw audio instead</li>
        </ul>
        <div className="mt-1">
          <a
            className="text-primary hover:underline"
            href="/samples/README.html"
          >
            View Sample Formats
          </a>
        </div>
      </div>
    </div>
  );

  // ---- Previews for selected (pending) files -------------------------------
  useEffect(() => {
    setTxPreview(null);
    setAudioMeta(null);
    if (!pendingFile) return;

    if (isAudioFile(pendingFile.name)) {
      const url = URL.createObjectURL(pendingFile);
      const audio = new Audio();
      audio.src = url;
      audio.preload = "metadata";
      const onLoaded = () => {
        setAudioMeta({
          duration: isFinite(audio.duration)
            ? Math.round(audio.duration)
            : undefined,
        });
        URL.revokeObjectURL(url);
      };
      const onError = () => {
        URL.revokeObjectURL(url);
      };
      audio.addEventListener("loadedmetadata", onLoaded);
      audio.addEventListener("error", onError);
      return () => {
        audio.removeEventListener("loadedmetadata", onLoaded);
        audio.removeEventListener("error", onError);
        try {
          URL.revokeObjectURL(url);
        } catch {
          // ignore URL revoke errors
        }
      };
    } else {
      pendingFile
        .text()
        .then(t => {
          const words = t.trim() ? t.trim().split(/\s+/).length : 0;
          const lines = t.split(/\r?\n/).filter(ln => ln.trim().length > 0);
          const first = lines.slice(0, 3).join(" ").slice(0, 180);
          setTxPreview({ words, preview: first });
        })
        .catch(() => setTxPreview({ words: 0, preview: "" }));
    }
  }, [pendingFile, isAudioFile]);

  // ---- Persist last filename + encounter type ------------------------------
  const LAST_FILE_KEY = "engage:lastUpload:lastFilename";
  const LAST_FILE_PING = "engage:lastUpload:ping";
  const LAST_TYPE_KEY = "engage:lastUpload:patientType";

  const loadLastFilename = () => {
    try {
      return localStorage.getItem(LAST_FILE_KEY) || "";
    } catch {
      return "";
    }
  };
  const saveLastFilename = (name: string) => {
    try {
      localStorage.setItem(LAST_FILE_KEY, name);
      localStorage.setItem(LAST_FILE_PING, String(Date.now()));
    } catch {
      // ignore storage errors
    }
  };

  const loadLastType = () => {
    try {
      return localStorage.getItem(LAST_TYPE_KEY) || "new";
    } catch {
      return "new";
    }
  };
  const saveLastType = (t: string) => {
    try {
      localStorage.setItem(LAST_TYPE_KEY, t);
    } catch {
      // ignore storage errors
    }
  };
  const [encType, setEncType] = useState<string>(loadLastType());

  useEffect(() => {
    setLastFilename(loadLastFilename());
    const onStorage = (e: StorageEvent) => {
      if (e.key === LAST_FILE_KEY || e.key === LAST_FILE_PING) {
        setLastFilename(loadLastFilename());
      }
      if (e.key === LAST_TYPE_KEY) {
        setEncType(loadLastType());
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // ---- Parsing / analysis helpers ------------------------------------------
  const estimateDurationMinutes = (tx: TranscriptLike): number | undefined => {
    if (!Array.isArray(tx.entries)) return undefined;
    let totalSec = 0;
    for (const e of tx.entries) {
      const hasBounds =
        typeof e?.start === "number" &&
        typeof e?.end === "number" &&
        e.end > e.start;
      if (hasBounds) totalSec += e.end - e.start;
      else if (typeof e?.text === "string" && e.text.trim()) {
        const words = e.text.trim().split(/\s+/).length;
        totalSec += (words / 150) * 60; // 150 wpm fallback
      }
    }
    return Math.max(1, Math.round(totalSec / 60));
  };

  const parseTextFile = async (file: File): Promise<Transcript> => {
    const text = await file.text();
    const name = file.name.toLowerCase();
    let format: "json" | "md" | "srt" | "txt" = "json";
    if (name.endsWith(".md")) format = "md";
    else if (name.endsWith(".srt")) format = "srt";
    else if (name.endsWith(".txt")) format = "txt";
    return parseFromString(text, format);
  };

  // ---- Main pipeline -------------------------------------------------------
  const handleFile = async (file: File) => {
    setError(null);
    setLoading(true);
    setLastFilename(file.name);
    saveLastFilename(file.name);

    // Type + size checks
    const audio = isAudioFile(file.name);
    const textTx = isTranscriptFile(file.name);

    if (!audio && !textTx) {
      setLoading(false);
      setError(renderUnsupported());
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    if (audio && file.size > MAX_AUDIO_BYTES) {
      setLoading(false);
      setError(renderTooLarge(bytesToMB(file.size), true));
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    if (textTx && file.size > MAX_TEXT_BYTES) {
      setLoading(false);
      setError(renderTooLarge(bytesToMB(file.size), false));
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    try {
      let transcript: Transcript;

      if (audio) {
        // 1) Send audio -> /transcribe
        const formData = new FormData();
        formData.append("file", file);

        const res = await fetch(`${BACKEND_URL}/transcribe`, {
          method: "POST",
          body: formData,
        });

        if (!res.ok) {
          // Map all transcription failures to the friendly message
          setError(renderTranscribeFailed());
          setShowResult(false);
          return;
        }
        transcript = (await res.json()) as Transcript;
      } else {
        // Parse text locally (.json / .md / .srt / .txt)
        try {
          transcript = await parseTextFile(file);
        } catch {
          setError(renderInvalidTranscript());
          setShowResult(false);
          return;
        }
      }

      // 2) Save transcript
      setTranscript(transcript);

      // 3) Ask backend to analyze
      // PACE backend expects { conversation: string } — plain text dialogue (speaker: text per line)
      const conversationText = transcript.entries
        .map(e => `${e.speaker}: ${e.text}`)
        .join("\n");

      let raw: unknown;

      try {
        const response = await apiClient.post<unknown>("/analyze/pace", {
          conversation: conversationText,
        });
        raw = response.data;
      } catch (error) {
        const authState = useAuthStore.getState();
        if (
          authState.mfaVerificationRequired ||
          authState.mfaEnrollmentRequired
        ) {
          setShowResult(false);
          return;
        }
        toast({
          title: "Transcript analysis failed",
          description: extractAxiosErrorMessage(
            error,
            "We couldn't analyze the transcript."
          ),
          variant: "destructive",
        });
        setShowResult(false);
        return;
      }

      const analysis: ServerAnalysis = isPaceResponse(raw)
        ? mapPaceResponseToServerAnalysis(raw)
        : raw;
      setServerAnalysis(analysis);

      // 4) build & save EncounterSnapshot for the Encounters list
      const minutesFromBackend =
        analysis?.duration?.minutes ??
        analysis?.result?.duration?.minutes ??
        analysis?.durationMinutes ??
        analysis?.meta?.duration?.minutes ??
        undefined;

      const estimatedMin = estimateDurationMinutes(transcript);
      const durationMin = minutesFromBackend ?? estimatedMin ?? 0;

      // Generate unique ID
      const newId =
        getPaceAnalysisId(raw) ??
        `tx-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

      const snapshot: EncounterSnapshot = {
        id: newId,
        ts: Date.now(),
        duration: durationMin,
        patientType:
          (encType?.toLowerCase?.() as EncounterSnapshot["patientType"]) ||
          (typeof analysis?.meta?.patientType === "string"
            ? analysis.meta.patientType.toLowerCase()
            : undefined) ||
          "other",
        talkClinicianPct: analysis?.talkTime?.clinicianPct ?? 0,
        talkPatientPct: analysis?.talkTime?.patientPct ?? 0,
        open: analysis?.questions?.open ?? 0,
        closed: analysis?.questions?.closed ?? 0,
        openPct: analysis?.questions?.openPct ?? 0,
        interruptionsCount: analysis?.interruptions?.count ?? 0,
        empathyCount: analysis?.empathy?.count ?? 0,
      };

      const existing = loadHistory();
      const filtered = existing.filter(
        e => Math.abs((e.ts || 0) - snapshot.ts) > 1000
      );
      saveHistory([snapshot, ...filtered]);

      // 5) Show results inline (and hide upload area)
      setShowResult(true);
    } catch (_err) {
      console.error(_err);
      setError(renderFallback());
      setShowResult(false);
    } finally {
      setLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const triggerFilePicker = () => fileInputRef.current?.click();

  const startAnother = () => {
    setOpenPickerAfterShow(true);
    setShowResult(false);
  };

  useEffect(() => {
    if (!showResult && openPickerAfterShow && fileInputRef.current) {
      fileInputRef.current.click();
      setOpenPickerAfterShow(false);
    }
  }, [showResult, openPickerAfterShow]);

  return (
    <div className="p-6 flex flex-col items-center gap-6">
      <PrivacyBanner compact />

      {/* Encounter type selector (persists last choice) */}
      {!showResult && (
        <div className="w-full max-w-xl -mb-2">
          <Label htmlFor="enc-type" className="text-xs text-muted-foreground">
            Encounter type
          </Label>
          <Select
            value={encType}
            onValueChange={v => {
              setEncType(v);
              saveLastType(v);
            }}
          >
            <SelectTrigger id="enc-type" className="mt-1">
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="annual">Annual Checkup</SelectItem>
              <SelectItem value="followup">Follow-up</SelectItem>
              <SelectItem value="new">New Patient</SelectItem>
              <SelectItem value="consultation">Consultation</SelectItem>
              <SelectItem value="emergency">Emergency</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Upload zone — HIDDEN once results are shown */}
      {!showResult && (
        <>
          <label
            className={`
              group relative overflow-hidden
              flex flex-col items-center justify-center
              border-2 border-dashed rounded-lg w-full max-w-xl min-h-48 text-center cursor-pointer
              transition-colors hover:border-primary hover:bg-muted/10
              ${dragOver ? "border-blue-500 bg-blue-500/5" : "border-muted-foreground/40"}
            `}
            title="Upload audio OR transcript"
            onDragOver={e => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragEnter={e => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => {
              e.preventDefault();
              setDragOver(false);
              const f = e.dataTransfer?.files?.[0];
              if (f) acceptAndQueue(f);
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".json,.md,.srt,.txt,.wav,.mp3,.m4a,.mp4,.webm,.ogg"
              className="hidden"
              onChange={e => {
                const file = e.target.files?.[0];
                if (!file) return;
                setError(null);
                acceptAndQueue(file);

                const audio = isAudioFile(file.name);
                const textTx = isTranscriptFile(file.name);

                if (!audio && !textTx) {
                  setError(renderUnsupported());
                  e.currentTarget.value = "";
                  return;
                }
                if (audio && file.size > MAX_AUDIO_BYTES) {
                  setError(renderTooLarge(bytesToMB(file.size), true));
                  e.currentTarget.value = "";
                  return;
                }
                if (textTx && file.size > MAX_TEXT_BYTES) {
                  setError(renderTooLarge(bytesToMB(file.size), false));
                  e.currentTarget.value = "";
                  return;
                }

                setPendingFile(file);
              }}
              disabled={loading}
            />

            {/* Top row: Audio OR Transcript */}
            <div className="pt-8 pb-3 flex items-center gap-3 text-foreground">
              <span className="inline-flex items-center gap-2 font-semibold">
                <Mic className="w-5 h-5" /> Audio
              </span>
              <span className="text-xs text-muted-foreground">OR</span>
              <span className="inline-flex items-center gap-2 font-semibold">
                <FileText className="w-5 h-5" /> Transcript
              </span>
            </div>

            {/* Instruction */}
            <p className="text-sm text-muted-foreground">
              Drag &amp; drop your file here or{" "}
              <span className="text-primary">click</span> to browse
            </p>

            {/* Supported formats */}
            <div className="mt-3 mb-4 text-xs text-muted-foreground">
              <div>Audio: WAV, MP3, M4A, MP4, WEBM, OGG</div>
              <div>Transcript: JSON, Markdown (.md), SRT, TXT</div>
            </div>

            {/* Minimal inline status */}
            {loading &&
              (() => {
                const f = fileInputRef.current?.files?.[0];
                if (!f) return null;
                const audio = isAudioFile(f.name);
                const sizeMB = f.size
                  ? (f.size / (1024 * 1024)).toFixed(1)
                  : undefined;
                return (
                  <div
                    className="mb-4 text-xs text-muted-foreground flex items-center gap-2"
                    aria-live="polite"
                  >
                    {audio ? (
                      <Mic className="w-4 h-4" />
                    ) : (
                      <FileText className="w-4 h-4" />
                    )}
                    <span className="truncate">
                      Selected: {f.name}
                      {typeof sizeMB !== "undefined" ? ` • ${sizeMB} MB` : ""}
                      {" — analyzing…"}
                    </span>
                    <Loader2 className="w-3 h-3 animate-spin" />
                  </div>
                );
              })()}

            {/* Last filename hint (when idle) */}
            {lastFilename && !loading ? (
              <div className="pb-4 text-xs text-muted-foreground/70">
                Last selected: {lastFilename}
              </div>
            ) : null}
          </label>

          {/* File preview BEFORE processing */}
          {pendingFile && !loading && (
            <div className="w-full max-w-xl border rounded-lg p-4 bg-muted/30">
              <div className="font-medium truncate">
                Selected: {pendingFile.name}
              </div>

              <div className="text-sm text-muted-foreground flex items-center gap-2 mt-1">
                {isAudioFile(pendingFile.name) ? (
                  <Mic className="w-4 h-4" />
                ) : (
                  <FileText className="w-4 h-4" />
                )}
                <span>
                  {isAudioFile(pendingFile.name) ? "Audio File" : "Transcript"}{" "}
                  •{" "}
                  {pendingFile.size >= 1024 * 1024
                    ? `${(pendingFile.size / 1024 / 1024).toFixed(1)} MB`
                    : `${Math.max(1, Math.round(pendingFile.size / 1024))} KB`}
                  {isAudioFile(pendingFile.name) && audioMeta?.duration
                    ? ` • ${formatDur(audioMeta.duration)}`
                    : ""}
                  {!isAudioFile(pendingFile.name) && txPreview
                    ? ` • ${txPreview.words.toLocaleString()} words`
                    : ""}
                </span>
              </div>

              {/* Transcript snippet */}
              {!isAudioFile(pendingFile.name) && txPreview?.preview && (
                <div className="mt-3 text-xs text-muted-foreground">
                  <div className="font-medium mb-1">Preview:</div>
                  <div className="line-clamp-3 italic">
                    &quot;{txPreview.preview}&quot;
                  </div>
                </div>
              )}

              <div className="flex gap-2 pt-3">
                <Button
                  variant="outline"
                  onClick={triggerFilePicker}
                  disabled={loading}
                >
                  Change File
                </Button>
                <Button
                  onClick={() => {
                    if (pendingFile) {
                      void handleFile(pendingFile);
                      setPendingFile(null);
                    }
                  }}
                  disabled={loading}
                >
                  {isAudioFile(pendingFile.name)
                    ? "Analyze Recording →"
                    : "Analyze Transcript →"}
                </Button>
              </div>
            </div>
          )}

          {/* Errors */}
          {error && (
            <div
              className="w-full max-w-xl border rounded-lg p-4 bg-destructive/5 text-destructive"
              aria-live="polite"
            >
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 mt-0.5" />
                <div className="space-y-2">
                  <div className="font-semibold">There was a problem</div>
                  <div className="text-sm space-y-2">{error}</div>
                </div>
              </div>
            </div>
          )}

          {/* Sample files link */}
          <div className="w-full max-w-xl text-xs text-muted-foreground ">
            <span className="mr-2">📥 Don’t have a file?</span>
            <a
              className="text-primary hover:underline"
              href="/samples/sample-audio.mp3"
              download
            >
              Sample audio (MP3)
            </a>{" "}
            •{" "}
            <a
              className="text-primary hover:underline"
              href="/samples/sample-transcript.txt"
              download
            >
              Sample transcript (txt)
            </a>
          </div>
        </>
      )}

      {/* Inline result */}
      {showResult && (
        <div className="w-full max-w-3xl mx-auto">
          <FeedbackCard />
          <div className="mt-4 flex gap-2 justify-center">
            <Button variant="default" onClick={startAnother} disabled={loading}>
              Analyze another encounter
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/engage/manager")}
              disabled={loading}
            >
              View all encounters
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/history")}
              disabled={loading}
            >
              View analysis history
            </Button>
          </div>
        </div>
      )}

      {/* Footer action while idle/loading (hidden when result is shown) */}
      {!showResult && (
        <Button
          className="mt-2"
          onClick={() => navigate("/engage/manager")}
          disabled={loading}
        >
          {loading ? "Working..." : "Cancel"}
        </Button>
      )}
    </div>
  );
}
